import { useQueryClient } from '@tanstack/react-query';
import { FileWarning, Pause, Play, RefreshCw } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { InlineError } from '@/components/ui/StatePanel';
import { useToast } from '@/components/ui/toast';

import {
  formatClock,
  liveSeconds,
  SESSION_LABELS,
  stageKindLabel,
} from '../time/clock-presentation';
import type { OpenClock, SessionState } from '../time/types';
import { useStageClock } from '../time/use-stage-clock';
import { useWorkHeartbeat } from '../time/use-work-heartbeat';

import { StageStandard } from './StageStandard';
import { skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

// Tarjeta "Tiempo de la etapa" del Editor (so=420, spec §4.1): el reloj pausable de la
// persona y el macro de la etapa, con Pausar y Reanudar. Los latidos salen de la
// interacción real mientras la sesión no está en pausa.

interface Props {
  flowInstanceId: string;
  companyId: string | null;
  /** Opera la etapa (responsable asignado o Calidad, G2-4): si no, no se mide ni se muestra. */
  canTrack: boolean;
  /** Permiso `quality:time-standards:read`: muestra el estándar de la etapa (spec
   *  2026-10-14 §7). */
  canSeeStandard?: boolean;
}

const OFFLINE_PAUSE = 'Sin conexión: la pausa se registrará al reconectar';

function pausesText(n: number): string {
  if (n === 0) {
    return 'Sin pausas';
  }
  return n === 1 ? 'Pausaste 1 vez' : `Pausaste ${n} veces`;
}

function ClockFace(props: {
  label: string;
  name: string;
  seconds: number;
  running: boolean;
  sub: string;
}): ReactElement {
  return (
    <div className={cn('clock', props.running && 'run')}>
      <div className="cl">
        {props.running ? <span className="dotb" aria-hidden="true" /> : null}
        {props.label}
      </div>
      <div className="big" role="timer" aria-label={props.name}>
        {formatClock(props.seconds)}
      </div>
      <div className="sub">{props.sub}</div>
    </div>
  );
}

function ClockSkeleton(): ReactElement {
  return (
    <div className="card rise" aria-hidden="true">
      <div className="cb clockcard">
        <span className={skeletonLine({ height: 'block' })} />
        <span className={skeletonLine({ height: 'block' })} />
      </div>
    </div>
  );
}

/** Tic de un segundo mientras la tarjeta muestra una etapa abierta. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/** Cada flujo tiene su propio estado (pausa pendiente, intención, latidos): cambiar de
 *  flujo sin desmontar el Editor no debe arrastrar el del anterior (so=420 M3). */
export function StageClockCard(props: Props): ReactElement {
  return <StageClock key={props.flowInstanceId} {...props} />;
}

function StageClock({
  flowInstanceId,
  companyId,
  canTrack,
  canSeeStandard = false,
}: Props): ReactElement | null {
  const clock = useStageClock(flowInstanceId, { companyId, enabled: canTrack });
  const qc = useQueryClient();
  const { showToast } = useToast();
  const data = clock.query.data;
  const open = data?.open === true ? data : null;
  const beat = useWorkHeartbeat({
    flowInstanceId,
    intervalSeconds: open?.heartbeatIntervalSeconds ?? 60,
    enabled: canTrack && open !== null && open.workStage && clock.session !== 'paused',
    onBeat: () => void clock.refresh(),
    onStageClosed: () => {
      void clock.refresh();
      void qc.invalidateQueries({ queryKey: ['flow-capabilities'] });
    },
  });
  const now = useNow(open !== null);

  if (!canTrack) {
    return null;
  }
  if (clock.query.isPending) {
    return <ClockSkeleton />;
  }
  if (clock.query.isError && data?.open !== true) {
    return (
      <section className="card rise" aria-labelledby="stage-clock-title">
        <div className="ch">
          <h3 id="stage-clock-title">Tiempo de la etapa</h3>
        </div>
        <InlineError
          icon={<FileWarning />}
          onRetry={() => void clock.query.refetch()}
          retrying={clock.query.isFetching}
        >
          No se pudo leer el tiempo de la etapa. {errorAdvice(clock.query.error)}
        </InlineError>
      </section>
    );
  }
  if (open === null) {
    return null;
  }

  const session: SessionState = clock.session ?? open.mine.session;
  const view: OpenClock = { ...open, mine: { ...open.mine, session } };
  const live = liveSeconds(view, clock.query.dataUpdatedAt, beat.lastBeatAt, now);

  const onPause = async (): Promise<void> => {
    const result = await clock.pause(beat.settle);
    if (result === 'error') {
      showToast('No se pudo pausar. Vuelve a intentar.', 'error');
    }
  };
  const onResume = async (): Promise<void> => {
    const result = await clock.resume();
    if (result === 'error') {
      showToast('No se pudo reanudar. Vuelve a intentar.', 'error');
    }
  };

  return (
    <section className="card rise" aria-labelledby="stage-clock-title">
      <div className="ch">
        <h3 id="stage-clock-title">Tiempo de la etapa</h3>
        <span className="count">
          {stageKindLabel(open.stageKind)}
          {open.attempt > 1 ? ` · intento ${open.attempt}` : ''}
        </span>
      </div>
      <div className="cb clockcard">
        {open.workStage ? (
          <ClockFace
            label="Mi tiempo · pausable"
            name="Mi tiempo en la etapa"
            seconds={live.mine}
            running={session === 'active'}
            sub={`${SESSION_LABELS[session]} · ${pausesText(open.mine.pauseCount)}`}
          />
        ) : null}
        <ClockFace
          label="Total · no se detiene"
          name="Tiempo total de la etapa"
          seconds={live.macro}
          running={false}
          sub={
            open.workStage
              ? 'Desde que empezó la etapa, con esperas y pausas.'
              : 'Esperando la fecha de vigencia'
          }
        />
        {open.workStage ? (
          <div className="clockacts">
            {session === 'paused' ? (
              <button
                type="button"
                className="btn sm"
                disabled={clock.busy}
                onClick={() => void onResume()}
              >
                <Play aria-hidden="true" />
                Reanudar
              </button>
            ) : (
              <button
                type="button"
                className="btn mut sm"
                disabled={clock.busy}
                onClick={() => void onPause()}
              >
                <Pause aria-hidden="true" />
                Pausar
              </button>
            )}
            {clock.pausePending ? (
              <p role="status" className="soft" style={{ fontSize: 11.5 }}>
                {OFFLINE_PAUSE}
              </p>
            ) : null}
          </div>
        ) : null}
        {open.workStage && canSeeStandard ? (
          <StageStandard
            flowInstanceId={flowInstanceId}
            companyId={companyId}
            stageKind={open.stageKind}
            effortSeconds={live.stage}
          />
        ) : null}
      </div>
      {clock.query.isError && (
        <InlineError
          icon={<RefreshCw size={16} />}
          onRetry={() => void clock.query.refetch()}
          retrying={clock.query.isFetching}
        >
          No se pudo actualizar el tiempo de la etapa. Se muestra el último leído.
          {refreshAdvice(clock.query.error)}
        </InlineError>
      )}
    </section>
  );
}
